import { useState } from "react";
import { Volume2, Loader2 } from "lucide-react";
import api from "../lib/api";

const LETTERS = "abcdefghijklmnopqrstuvwxyz".split("");
const DIGRAPHS = ["sh", "ch", "th", "wh", "ph", "ng", "ck", "qu"];

// Level 2 — long-vowel patterns + r-controlled vowels. Display as-is; the
// backend maps each to its phoneme spelling. Split digraphs use an underscore
// (rendered as "a-e") so learners see the "silent e" convention.
const LONG_VOWELS = [
    "a_e", "i_e", "o_e", "u_e",
    "ee", "ea", "oa", "ai", "ay", "ie", "ue",
    "igh", "oo", "ow", "ou", "oi", "oy",
];
const R_CONTROLLED = ["ar", "er", "ir", "or", "ur"];

/**
 * SoundStrip — tap any letter (or common digraph) to hear its phoneme.
 * Level 1: single letters + consonant digraphs.
 * Level 2: long-vowel patterns + r-controlled vowels.
 *
 * Server-side caches every unique phoneme, so this UI is essentially free
 * to use after each grapheme has been played once (per voice).
 */
export default function SoundStrip() {
    const [playing, setPlaying] = useState(null);
    const [level, setLevel] = useState(1);

    const play = async (g) => {
        if (playing) return;
        setPlaying(g);
        try {
            const { data } = await api.post("/tts", { text: g, mode: "phoneme" });
            const audio = new Audio(`data:${data.mime};base64,${data.audio_base64}`);
            await audio.play().catch(() => {});
        } catch (e) {
            console.error("Phoneme playback failed", e);
        } finally {
            setPlaying(null);
        }
    };

    const tile = (g) => {
        const isBusy = playing === g;
        const label = g.includes("_") ? g.replace("_", "-") : g;
        return (
            <button
                key={g}
                onClick={() => play(g)}
                disabled={!!playing}
                data-testid={`sound-strip-${g}`}
                aria-label={`Play the sound of ${label}`}
                className="tile w-14 h-14 sm:w-16 sm:h-16 text-2xl sm:text-3xl flex flex-col items-center justify-center relative disabled:opacity-60"
                style={{ fontFamily: "Outfit" }}
            >
                <span>{label}</span>
                <span className="absolute bottom-1 right-1.5 text-[#2D5D4E]">
                    {isBusy ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.8} />
                    ) : (
                        <Volume2 className="w-3.5 h-3.5" strokeWidth={1.8} />
                    )}
                </span>
            </button>
        );
    };

    const LevelToggle = () => (
        <div
            className="inline-flex rounded-lg border border-[#E5E7E2] bg-white p-1 mb-6"
            role="tablist"
            data-testid="sound-strip-level-toggle"
        >
            {[1, 2].map((n) => (
                <button
                    key={n}
                    onClick={() => setLevel(n)}
                    data-testid={`sound-strip-level-${n}`}
                    role="tab"
                    aria-selected={level === n}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        level === n
                            ? "bg-[#2D5D4E] text-[#FAF9F6]"
                            : "text-[#515B57] hover:text-[#1C2321]"
                    }`}
                >
                    Level {n}
                </button>
            ))}
        </div>
    );

    return (
        <section
            className="card-rw p-6 md:p-8 mb-10"
            data-testid="sound-strip"
            style={{ borderLeft: "6px solid #2D5D4E" }}
        >
            <p className="label-eyebrow mb-2">Practice · single sounds</p>
            <h2
                className="text-2xl font-medium text-[#1C2321] mb-3"
                style={{ fontFamily: "Outfit" }}
            >
                Tap a letter to hear its sound.
            </h2>
            <p className="text-[#515B57] leading-relaxed mb-4">
                These are the sounds, not the letter names — the same sounds you&apos;ll blend
                together to read a word.
            </p>

            <LevelToggle />

            {level === 1 ? (
                <>
                    <div className="flex flex-wrap gap-2 mb-5">{LETTERS.map(tile)}</div>
                    <p className="label-eyebrow mb-2 mt-6">Common digraphs</p>
                    <div className="flex flex-wrap gap-2">{DIGRAPHS.map(tile)}</div>
                </>
            ) : (
                <>
                    <p className="label-eyebrow mb-2">Long vowel teams &amp; silent-e</p>
                    <div className="flex flex-wrap gap-2 mb-5">{LONG_VOWELS.map(tile)}</div>
                    <p className="label-eyebrow mb-2 mt-6">R-controlled vowels</p>
                    <div className="flex flex-wrap gap-2">{R_CONTROLLED.map(tile)}</div>
                </>
            )}
        </section>
    );
}
